// Shared puzzle engine: the client plays with it, the server replays with it.
// The server keeps a verbatim copy at server/engine.js (tools/sync-catalog.mjs).
//
// Board configs (content.js `tiers`):
//   mosaic   { cols, scramble }  sliding puzzle, the blank is the last index
//   rings    { count, step }     concentric rings turned by `step` degrees
//   layers   { bands }           horizontal bands shifted in 8 px steps
//   swap     { cols }            square grid, swap two tiles
//   rotate   { cols }            square grid, quarter turns
//   sequence { strips }          vertical strips, swap two

export const LAYER_STEP = 8;
export const LAYER_LIMIT = 56;

export class MoveError extends Error {
  constructor(key) {
    super(key);
    this.key = key;
  }
}

export function seedFrom(value) {
  return [...String(value)].reduce((seed, char) => Math.imul(seed ^ char.charCodeAt(0), 16777619) >>> 0, 2166136261);
}

export function seededRandom(value) {
  let seed = seedFrom(value) || 1;
  return () => {
    seed = Math.imul(seed ^ seed >>> 15, 1 | seed);
    seed ^= seed + Math.imul(seed ^ seed >>> 7, 61 | seed);
    return ((seed ^ seed >>> 14) >>> 0) / 4294967296;
  };
}

export function shuffledOrder(size, salt) {
  const values = Array.from({ length: size }, (_, index) => index);
  const random = seededRandom(salt);
  for (let index = size - 1; index > 0; index--) {
    const target = Math.floor(random() * (index + 1));
    [values[index], values[target]] = [values[target], values[index]];
  }
  if (values.every((value, index) => value === index)) [values[0], values[1]] = [values[1], values[0]];
  return values;
}

export const mosaicNeighbours = (position, cols) => {
  const row = Math.floor(position / cols), column = position % cols;
  const options = [];
  if (row > 0) options.push(position - cols);
  if (row < cols - 1) options.push(position + cols);
  if (column > 0) options.push(position - 1);
  if (column < cols - 1) options.push(position + 1);
  return options;
};

export function shuffledMosaic(salt, { cols, scramble }) {
  const size = cols * cols;
  const tiles = Array.from({ length: size }, (_, index) => index);
  let blank = size - 1;
  const random = seededRandom(salt);
  let previous = -1;
  for (let step = 0; step < scramble; step++) {
    const choices = mosaicNeighbours(blank, cols).filter(item => item !== previous);
    const next = choices[Math.floor(random() * choices.length)];
    [tiles[blank], tiles[next]] = [tiles[next], tiles[blank]];
    previous = blank;
    blank = next;
  }
  if (tiles.every((value, index) => value === index)) return shuffledMosaic(`${salt}:again`, { cols, scramble });
  return tiles;
}

export const blankIndex = level => level.config.cols * level.config.cols - 1;

export function initialState(level, seed) {
  const salt = `${level.id}:${seed}`;
  const random = seededRandom(salt);
  const config = level.config;
  if (level.mechanic === "mosaic") return shuffledMosaic(salt, config);
  if (level.mechanic === "rings") {
    const positions = 360 / config.step;
    return Array.from({ length: config.count }, () => (1 + Math.floor(random() * (positions - 1))) * config.step);
  }
  if (level.mechanic === "layers") return Array.from({ length: config.bands }, () => (Math.floor(random() * 9) - 4 || 2) * LAYER_STEP);
  if (level.mechanic === "swap") return shuffledOrder(config.cols * config.cols, salt);
  if (level.mechanic === "rotate") return Array.from({ length: config.cols * config.cols }, () => (1 + Math.floor(random() * 3)) * 90);
  return shuffledOrder(config.strips, salt);
}

// One entry per position; for the mosaic the blank counts as settled when it is home.
export function restorationMask(level, values) {
  if (level.mechanic === "rings" || level.mechanic === "rotate") return values.map(value => value % 360 === 0);
  if (level.mechanic === "layers") return values.map(value => Math.abs(value) <= 4);
  return values.map((value, index) => value === index);
}

export const solved = (level, values) => restorationMask(level, values).every(Boolean);

// Number of visible pieces (the mosaic blank is not a piece).
export const pieceTotal = level => (level.mechanic === "mosaic" ? level.config.cols * level.config.cols - 1 : level.mechanic === "rings" ? level.config.count : level.mechanic === "layers" ? level.config.bands : level.mechanic === "sequence" ? level.config.strips : level.config.cols * level.config.cols);

export const restoredCount = (level, values, mask = restorationMask(level, values)) => mask.filter((value, index) => value && !(level.mechanic === "mosaic" && values[index] === blankIndex(level))).length;

export function applyEvent(level, values, event) {
  const payload = event.payload || {};
  const config = level.config;
  const next = [...values];
  if (level.mechanic === "mosaic" && event.type === "slide") {
    const blank = next.indexOf(blankIndex(level));
    if (!Number.isInteger(payload.position) || !mosaicNeighbours(blank, config.cols).includes(payload.position)) throw new MoveError("replay.mosaic");
    [next[payload.position], next[blank]] = [next[blank], next[payload.position]];
  } else if (level.mechanic === "rings" && event.type === "ring") {
    if (!Number.isInteger(payload.index) || payload.index < 0 || payload.index >= config.count) throw new MoveError("replay.ring");
    next[payload.index] = (next[payload.index] + config.step) % 360;
  } else if (level.mechanic === "layers" && event.type === "layer-shift") {
    if (!Number.isInteger(payload.index) || payload.index < 0 || payload.index >= config.bands || ![-LAYER_STEP, LAYER_STEP].includes(payload.amount)) throw new MoveError("replay.layerShift");
    next[payload.index] = Math.max(-LAYER_LIMIT, Math.min(LAYER_LIMIT, next[payload.index] + payload.amount));
  } else if (level.mechanic === "layers" && event.type === "layer") {
    if (!Number.isInteger(payload.index) || payload.index < 0 || payload.index >= config.bands || !Number.isInteger(payload.offset) || payload.offset % LAYER_STEP !== 0 || Math.abs(payload.offset) > LAYER_LIMIT) throw new MoveError("replay.layer");
    next[payload.index] = payload.offset;
  } else if ((level.mechanic === "swap" && event.type === "swap") || (level.mechanic === "sequence" && event.type === "sequence")) {
    const limit = next.length - 1;
    if (![payload.first, payload.second].every(value => Number.isInteger(value) && value >= 0 && value <= limit) || payload.first === payload.second) throw new MoveError("replay.swap");
    [next[payload.first], next[payload.second]] = [next[payload.second], next[payload.first]];
  } else if (level.mechanic === "rotate" && event.type === "rotate") {
    if (!Number.isInteger(payload.position) || payload.position < 0 || payload.position >= config.cols * config.cols) throw new MoveError("replay.rotate");
    next[payload.position] = (next[payload.position] + 90) % 360;
  } else {
    throw new MoveError("replay.mismatch");
  }
  return next;
}

// Which piece a hint should point at: index (and partner for swaps), or null when solved.
export function hintTarget(level, values) {
  const mask = restorationMask(level, values);
  if (level.mechanic === "swap" || level.mechanic === "sequence") {
    const index = values.findIndex((value, position) => value !== position);
    return index < 0 ? null : { index, partner: values.indexOf(index) };
  }
  if (level.mechanic === "mosaic") {
    const blank = values.indexOf(blankIndex(level));
    const candidates = mosaicNeighbours(blank, level.config.cols);
    const target = candidates.find(position => values[position] === blank) ?? candidates[0];
    return { index: target };
  }
  const index = mask.findIndex(value => !value);
  return index < 0 ? null : { index };
}
